/** Placeholder inside `[...]` maps to a picker menu. */
export type PromptSlotKind =
    | 'category'
    | 'supplier'
    | 'material'
    | 'plant'
    | 'cost_center'
    | 'generic';

export type PromptSegment =
    | {type: 'text'; text: string}
    | {
        type: 'slot';
        id: string;
        kind: PromptSlotKind;
        /** Raw label from brackets, e.g. `Category` */
        label: string;
        /** User-selected value replacing the slot; `null` until chosen. */
        value: string | null;
    };

const PLACEHOLDER_RE = /\[([^\]]+)\]/g;

export function promptContainsPlaceholders(raw: string): boolean {
    return /\[[^\]]+\]/.test(raw);
}

/** Display form: `[supplier]` → `/supplier` (label trimmed inside brackets). */
export function formatPromptWithSlashCommands(raw: string): string {
    return raw.replace(PLACEHOLDER_RE, (_, label) => `/${label.trim()}`);
}

export function inferSlotKind(label: string): PromptSlotKind {
    const s = label.trim().toLowerCase();
    if (s.includes('category')) {
        return 'category';
    }
    if (s.includes('supplier')) {
        return 'supplier';
    }
    if (s.includes('material')) {
        return 'material';
    }
    if (s.includes('plant')) {
        return 'plant';
    }
    if (s.includes('cost center') || s.includes('cost_center')) {
        return 'cost_center';
    }
    return 'generic';
}

/**
 * Split prompt text into plain runs and `[label]` slots.
 */
export function parsePromptToSegments(raw: string): PromptSegment[] {
    const segments: PromptSegment[] = [];
    let lastIndex = 0;
    let slotIndex = 0;
    let m: RegExpExecArray | null;
    const re = new RegExp(PLACEHOLDER_RE.source, 'g');
    while ((m = re.exec(raw)) !== null) {
        if (m.index > lastIndex) {
            segments.push({type: 'text', text: raw.slice(lastIndex, m.index)});
        }
        const label = m[1].trim();
        segments.push({
            type: 'slot',
            id: `slot-${slotIndex++}`,
            kind: inferSlotKind(label),
            label,
            value: null,
        });
        lastIndex = m.index + m[0].length;
    }
    if (lastIndex < raw.length) {
        segments.push({type: 'text', text: raw.slice(lastIndex)});
    }
    return segments;
}

/** Build string for sending to the chat API (resolved values or fallback to brackets). */
export function segmentsToResolvedString(segments: PromptSegment[]): string {
    return segments
        .map((s) => {
            if (s.type === 'text') {
                return s.text;
            }
            return s.value ?? `[${s.label}]`;
        })
        .join('');
}
